import { noop, pull } from 'lodash';
import { inject, provide } from 'vue';

export interface ValidationHook {
  (): Promise<boolean>;
}

export interface SubmitHook {
  (): void | Promise<void>;
}

export class FormController {
  private submitting = false;
  private validationHooks: Array<ValidationHook> = [];
  disabled = false;

  constructor(
    private submitHook: SubmitHook = noop,
    private validationFailureHook = noop,
  ) {
  }

  addValidationHook(hook: ValidationHook): void {
    this.validationHooks.push(hook);
  }

  removeValidationHook(hook: ValidationHook): void {
    pull(this.validationHooks, hook);
  }

  async validate(): Promise<boolean> {
    const results = await Promise.all(this.validationHooks.map(h => h()));
    const failed = results.includes(false);
    if (failed) this.validationFailureHook();
    return !failed;
  }

  async submit(): Promise<void> {
    if (this.disabled || this.submitting || !await this.validate()) return;
    try {
      this.submitting = true;
      await this.submitHook();
    } finally {
      this.submitting = false;
    }
  }
}

const FORM_CONTROLLER_KEY = Symbol('formController');

export function setFormController(controller: FormController): void {
  provide(FORM_CONTROLLER_KEY, controller);
}

export function getFormController(): FormController | undefined {
  return inject(FORM_CONTROLLER_KEY, undefined); // passing undefined as default avoids injection warning
}
